import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useFormik } from "formik";
import { Link } from "react-router-dom";
import { login } from "../../actions/auth";
import * as Yup from "yup";
import TokenService from "../../services/token";
import rutas from "../../routes/constants/routes";
import LockIcon from "@mui/icons-material/Lock";
// @ts-ignore
import ReCAPTCHA from "react-google-recaptcha";

const { REACT_APP_CAPTCHA_APIKEY } = process.env;

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface DocType {
  id: string | number;
  name: string;
}

interface ApiError {
  response?: {
    data?: string;
  };
}

interface LoginFormValues {
  identification: string;
  email: string;
  password: string;
  captchaToken: string;
}

interface LoginStateProps {
  session?: unknown;
  success?: boolean;
  loading?: boolean;
  data?: unknown;
  docType?: DocType[];
  error?: ApiError;
}

interface LoginDispatchProps {
  login: (loginData: {
    identification: string;
    email: string;
    password: string;
    captchaToken: string;
  }) => void;
}

type LoginProps = LoginStateProps & LoginDispatchProps;

/* -------------------------------------------------------------------------- */
/*                                Component                                   */
/* -------------------------------------------------------------------------- */

const Login: React.FC<LoginProps> = (props) => {
  useEffect(() => {
    if (props.success) {
      if (TokenService.hasSession()) {
        window.location.assign(rutas.DASHBOARD);
      }
    }
    if (TokenService.hasSession()) {
      window.location.assign(rutas.DASHBOARD);
    }
  }, [props]);

  const formatCuit = (value: string): string => {
    // Solo deja números
    const onlyNums = value.replace(/\D/g, "");

    if (onlyNums.length <= 2) return onlyNums;
    if (onlyNums.length <= 10)
      return `${onlyNums.slice(0, 2)}-${onlyNums.slice(2)}`;
    return `${onlyNums.slice(0, 2)}-${onlyNums.slice(2, 10)}-${onlyNums.slice(
      10,
      11,
    )}`;
  };

  const handleCuitChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const formatted = formatCuit(e.target.value);
    formik.setFieldValue("identification", formatted);
  };

  const formik = useFormik<LoginFormValues>({
    initialValues: {
      identification: "",
      email: "",
      password: "",
      captchaToken: "",
    },
    validationSchema: Yup.object({
      identification: Yup.string()
        .required("El número de documento es obligatorio")
        .matches(
          /^\d{2}-\d{8}-\d{1}$/,
          "Formato inválido. Debe ser xx-xxxxxxxx-x",
        ),
      password: Yup.string().required("Ingrese su contraseña"),
      email: Yup.string().required("El email es obligatorio"),
      captchaToken: Yup.string().required("Captcha requerido"),
    }),
    onSubmit: (formData) => {
      props.login({
        ...formData,
        identification: formData.identification.replace(/-/g, ""),
      });
    },
  });

  return (
    <>
      <div>
        <div className="d-grid gap-2 col-12 col-md-4 mx-auto">
          <form
            className="container"
            style={{ marginBottom: "2.5rem" }}
            onSubmit={formik.handleSubmit}
          >
            <div>
              <div
                style={{
                  height: "3.438rem",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "3rem",
                }}
              >
                <div
                  style={{
                    width: "45px",
                    height: "45px",
                    background: "#E3000B",
                    borderRadius: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <LockIcon style={{ fill: "white" }}></LockIcon>
                </div>
              </div>

              <h5
                className="text-center"
                style={{ marginTop: "1rem", marginBottom: "1.875rem" }}
              >
                Complete sus datos para iniciar sesión
              </h5>

              <div className="mb-3">
                <label
                  htmlFor="email"
                  className="form-label"
                  style={{ color: "gray", fontWeight: 600 }}
                >
                  Email
                </label>
                <input
                  type="email"
                  onChange={formik.handleChange}
                  name="email"
                  id="email"
                  className="form-control"
                  style={{ borderColor: "black", padding: "0.625rem" }}
                ></input>
                {formik.touched.email && formik.errors.email ? (
                  <div className="text-danger">{formik.errors.email}</div>
                ) : null}
              </div>

              <div className="mb-3">
                <label
                  htmlFor="email"
                  className="form-label"
                  style={{ color: "gray", fontWeight: 600 }}
                >
                  Número de documento{" "}
                  <span style={{ fontWeight: "lighter" }}>
                    (Sin puntos ni guiones)
                  </span>{" "}
                </label>
                <input
                  name="identification"
                  className="form-control"
                  id="identification"
                  style={{ borderColor: "black", height: "2.5rem" }}
                  onChange={handleCuitChange}
                  value={formik.values.identification}
                  maxLength={13}
                />
                {formik.touched.identification &&
                formik.errors.identification ? (
                  <div className="text-danger">
                    {formik.errors.identification}
                  </div>
                ) : null}
              </div>

              <div className="mb-3">
                <label
                  htmlFor="password"
                  className="form-label"
                  style={{ color: "gray", fontWeight: 600 }}
                >
                  Contraseña
                </label>
                <input
                  type="password"
                  onChange={formik.handleChange}
                  name="password"
                  id="password"
                  className="form-control"
                  style={{ borderColor: "black", padding: "0.625rem" }}
                ></input>
                {formik.touched.password && formik.errors.password ? (
                  <div className="text-danger">{formik.errors.password}</div>
                ) : null}
              </div>

              <div className="mb-12">
                <ReCAPTCHA
                  sitekey={REACT_APP_CAPTCHA_APIKEY ?? ""}
                  style={{ margin: "0 auto", display: "table" }}
                  onChange={(value: string | null) => {
                    formik.setFieldValue("captchaToken", value ?? "");
                    formik.setSubmitting(false);
                  }}
                />
                {formik.touched.captchaToken && formik.errors.captchaToken ? (
                  <div className="text-danger text-center">
                    {formik.errors.captchaToken}
                  </div>
                ) : null}
              </div>

              {props.error ? (
                <div
                  className="alert alert-danger"
                  style={{
                    maxWidth: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  role="alert"
                >
                  {props.error.response?.data}
                </div>
              ) : (
                ""
              )}

              <div
                style={{
                  maxWidth: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <button
                  className="btn btn-danger"
                  style={{
                    backgroundColor: "#E3000B",
                    padding: "0.500rem 0.625rem",
                    margin: "1rem 0rem",
                    width: "100%",
                  }}
                  type="submit"
                >
                  Iniciar sesión
                </button>
              </div>

              <Link to={rutas.RESET_PASWORD} style={{ borderColor: "black" }}>
                Recuperar contraseña
              </Link>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*                              Redux                                         */
/* -------------------------------------------------------------------------- */

interface RootState {
  auth: {
    session?: unknown;
    success?: boolean;
    loading?: boolean;
    data?: unknown;
    error?: ApiError;
  };
  params: {
    docType?: DocType[];
  };
}

const mapStateToProps = (state: RootState): LoginStateProps => ({
  session: state.auth.session,
  success: state.auth.success,
  loading: state.auth.loading,
  data: state.auth.data,
  docType: state.params.docType,
  error: state.auth.error,
});

const mapDispatchToProps = (dispatch: any): LoginDispatchProps => ({
  login: (loginData) => dispatch(login(loginData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
